import React, { ReactNode, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { LucideIcon, Menu, X } from 'lucide-react';
import { Logo } from '../Logo';
import { NotificationsButton } from './NotificationsButton';
import { ProfileMenu } from './ProfileMenu';
import { SupportBotButton } from './SupportBotButton';
import { paths } from 'shared/routing/paths';

export interface NavItem {
  label: string;
  to: string;
  /** Optional leading icon, rendered before the label. */
  icon?: LucideIcon;
}

interface AppShellProps {
  /** Sidebar navigation items for the current role area. */
  nav?: NavItem[];
  /** Optional content above the sidebar navigation, such as a role-specific create menu. */
  sidebarHeader?: ReactNode;
  /** Hide the persistent sidebar on narrow screens while retaining its links in a mobile menu. */
  hideSidebarOnMobile?: boolean;
  /** Optional role-specific control rendered before the global header actions. */
  headerActions?: ReactNode;
  /** Drop the max-w-6xl cap so the header + content span the full browser
   *  width — for content-dense pages (e.g. campaigns, message board) that
   *  benefit from the extra space instead of the centered, ~1152px column. */
  fullWidth?: boolean;
  /** Give a workspace the exact space below the global header. */
  fullHeight?: boolean;
  /** Phones only (below md): drop the page gutters and hand the content the
   *  exact space below the header as a flex column, so an app-like page such
   *  as the message board can run edge to edge and pin its own footer. From
   *  md up the shell lays out exactly as it does without this flag. */
  fullBleedOnMobile?: boolean;
  children: ReactNode;
}

/**
 * Base authenticated layout: sticky navy header + optional role sidebar + content
 * column. Per-role shells (expert/manager/admin/operations) compose this with
 * their own nav. Mirrors workbench's header/sidebar conventions and the primary
 * navy theme.
 */
export function AppShell({
  nav,
  sidebarHeader,
  hideSidebarOnMobile = false,
  fullWidth,
  fullHeight,
  fullBleedOnMobile = false,
  headerActions,
  children,
}: AppShellProps) {
  // max-w-full (not max-w-none) so the width is always an interpolatable
  // length — `none` can't be animated between, it just snaps.
  const widthClass = `transition-[max-width] duration-300 ease-in-out ${fullWidth ? 'max-w-full' : 'max-w-6xl'}`;
  const contentHeightClass = fullHeight
    ? 'h-[calc(100dvh-4rem)] min-h-0'
    : fullBleedOnMobile
      ? 'h-[calc(100dvh-4rem)] min-h-0 md:h-auto md:flex-1'
      : 'flex-1';
  const contentGutterClass = fullBleedOnMobile
    ? `px-0 md:px-6 lg:px-8 ${fullHeight ? 'py-0' : 'py-0 md:py-6'}`
    : `px-4 sm:px-6 lg:px-8 ${fullHeight ? 'py-0' : 'py-4 md:py-6'}`;
  const contentColumnClass = fullHeight || fullBleedOnMobile ? 'flex min-h-0 flex-col' : '';
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const hasMobileNav = hideSidebarOnMobile && nav && nav.length > 0;
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="sticky top-0 z-30 h-16 bg-primary text-white print:hidden">
        <div className={`relative mx-auto flex h-full ${widthClass} items-center justify-between px-4 sm:px-6 lg:px-8`}>
          <div className="flex min-w-0 items-center gap-2">
            {hasMobileNav ? (
              <button
                type="button"
                aria-label={mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={mobileNavOpen}
                aria-controls="mobile-app-navigation"
                onClick={() => setMobileNavOpen((open) => !open)}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-white/90 hover:bg-white/10 hover:text-white md:hidden"
              >
                {mobileNavOpen ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
              </button>
            ) : null}
            <NavLink
              to={paths.expert.root}
              aria-label="Go to Expert Dashboard home"
              className="rounded-sm focus:outline-none focus:ring-2 focus:ring-white/50"
            >
              <Logo className="pl-2.5 text-2xl font-bold" />
            </NavLink>
          </div>
          <div className="flex min-w-0 items-center gap-1">
            {headerActions ? <div className="min-w-0">{headerActions}</div> : null}
            <div className="flex items-center gap-1">
              <SupportBotButton />
              <NotificationsButton />
              <ProfileMenu />
            </div>
          </div>
          {hasMobileNav && mobileNavOpen ? (
            <nav
              id="mobile-app-navigation"
              aria-label="Mobile navigation"
              className="absolute inset-x-0 top-full max-h-[calc(100dvh-4rem)] overflow-y-auto border-b border-slate-200 bg-white px-4 py-3 text-slate-700 shadow-lg md:hidden"
            >
              <div className="flex flex-col gap-1">
                {nav.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end
                    onClick={() => setMobileNavOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium ${
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`
                    }
                  >
                    {item.icon ? <item.icon className="h-4 w-4 shrink-0" /> : null}
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </nav>
          ) : null}
        </div>
      </header>

      <div
        className={`mx-auto flex w-full ${widthClass} ${contentHeightClass} gap-0 md:gap-6 ${contentGutterClass}`}
        data-testid="app-shell-content"
      >
        {nav && nav.length > 0 ? (
          <aside
            className={`${hideSidebarOnMobile ? 'hidden md:flex' : 'flex'} sticky top-16 h-[calc(100vh-4rem)] w-56 shrink-0 flex-col py-6 print:hidden ${
              fullHeight ? '' : '-my-6'
            }`}
          >
            {sidebarHeader ? <div className="mb-4">{sidebarHeader}</div> : null}
            <nav className="flex flex-col gap-1">
              {nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`
                  }
                >
                  {item.icon ? <item.icon className="h-4 w-4 shrink-0" /> : null}
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <footer className="mt-auto px-3 pb-1 text-xs text-slate-500">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Link to={paths.public.terms} className="hover:text-slate-900 hover:underline">
                  Terms
                </Link>
                <span aria-hidden="true">|</span>
                <Link to={paths.public.privacy} className="hover:text-slate-900 hover:underline">
                  Privacy Policy
                </Link>
                <span aria-hidden="true">|</span>
                <a href={paths.public.status} className="hover:text-slate-900 hover:underline">
                  System status
                </a>
              </div>
              <p className="mt-2">© {new Date().getFullYear()} Aligned Labs, Inc.</p>
            </footer>
          </aside>
        ) : null}

        <main className={`min-w-0 w-full flex-1 ${contentColumnClass}`}>{children}</main>
      </div>
    </div>
  );
}
